import {storeInstance} from '@aofl/store/dist/legacy';
import {productStatuses, sdoNs, statsProducts} from './constants-enumerate';
import {deepAssign} from '@aofl/object-utils';
import {getLocaleDate} from './get-locale-date';
import {dateTimeFormatter} from '@modules/i18n-datetime';
import {getSortedProducts} from '@modules/product-helper';

const sdo = {
  namespace: sdoNs.STUDENT_INFO,
  mutations: {
    init() {
      return {
        id: 0,
        state_id: '',
        sis_id: '',
        first_name: '',
        last_name: '',
        external_id: '',
        preferred_communication: 'email',
        phone: '',
        email: '',
        classes: [],
        grade: null,
        language: null,
        birth_date: null,
        gender: '',
        masked_email: '',
        group_info: {},
        products: [],
        schools: [],
        teachers: {},
        has_contact_info: false,
        is_third_party_connected: false,
        student_has_changes: false,
        managed_by: null,
        dashboardGroups: [],
        demographics: null,
        caregivers: [],
        uuid: '',
      };
    },
    setData(subState, {
      id, first_name, last_name = '', external_id, preferred_communication, phone, email, // eslint-disable-line
      classes, grade, gender, masked_email, birth_date, language, products, schools, teachers, has_contact_info,
      is_third_party_connected, group_info, status, name, managed_by, archived_timestamp, dashboard_groups,
      demographics, state_id, sis_id, caregivers, school_ids, uuid
    }) { // eslint-disable-line
      if (!name) {
        name = first_name + ' ' + last_name;
      }
      return Object.assign({}, subState, {
        id,
        first_name,
        last_name,
        name,
        external_id,
        preferred_communication,
        phone,
        email,
        classes: classes || [],
        grade,
        language,
        gender,
        birth_date,
        masked_email,
        products: products || [],
        schools: schools || [],
        teachers,
        has_contact_info,
        group_info: group_info || {},
        is_third_party_connected,
        student_has_changes: false,
        status,
        managed_by,
        archived_timestamp,
        dashboardGroups: dashboard_groups || [],
        demographics,
        state_id,
        sis_id,
        caregivers: caregivers || [],
        school_ids: school_ids || [],
        uuid: uuid || '',
      });
    },
    reset(subState) {
      return Object.assign({}, subState, {
        id: null,
        first_name: '',
        last_name: '',
        external_id: '',
        preferred_communication: '',
        phone: '',
        email: '',
        grade: '',
        language: '',
        gender: '',
        birth_date: null,
        masked_email: '',
        teachers: [],
        has_contact_info: false,
        group_info: {},
        is_third_party_connected: false,
        classes: [],
        schools: [],
        products: [],
        student_has_changes: false,
        demographics: null,
        archived_timestamp: null,
        state_id: '',
        sis_id: '',
        caregivers: [],
        school_ids: [],
        uuid: '',
      });
    },
    updateDemographic(subState, demographic) {
      const demographics = {...subState.demographics};
      demographics[demographic.field] = demographic.value;

      return Object.assign({}, subState, {demographics});
    },
    addProduct(subState, product) {
      const products = subState.products;
      let append = true;
      for (let i = 0; i < products.length; i++) {
        if (products[i].product === product.product) {
          subState.products[i] = product;
          append = false;
          break;
        }
      }

      if (append) {
        subState.products.push(product);
      }

      return Object.assign({}, subState, {
        products: [
          ...subState.products,
        ]
      });
    },
    removeProduct(subState, payload) {
      let products = subState.products;
      products = products.filter(item => item.product !== payload.product);
      return Object.assign({}, subState, {
        products: [
          ...products,
        ]
      });
    },
    addGroups(subState, groups) {
      const allGroups = {...(subState.group_info || {})};
      groups.map((item) => {
        allGroups[item.id] = item;
      });

      return Object.assign({}, subState, {group_info: allGroups});
    },
    addClass(subState, classItem) {
      const classes = [
        ...subState.classes,
        classItem
      ];
      const uniqueClasses = {};
      classes.map((item) => {
        uniqueClasses[item.id] = item;
      });
      return Object.assign({}, subState, {
        classes: Object.values(uniqueClasses)
      });
    },
    removeClass(subState, index) {
      return Object.assign({}, subState, {
        classes: [
          ...subState.classes.slice(0, index),
          ...subState.classes.slice(index + 1)
        ]
      });
    },
    setHasChanges(subState, changed) {
      return Object.assign({}, subState, {student_has_changes: changed});
    },
    setCaregiverInfo(subState, payload) {
      const caregivers = [...subState.caregivers];
      if (typeof caregivers[payload.index] === 'undefined') {
        caregivers[payload.index] = {
          first_name: '',
          contact_email: '',
        };
      }
      caregivers[payload.index] = {
        ...caregivers[payload.index],
        [payload.field]: payload.value
      };
      return Object.assign({}, subState, {caregivers});
    }
  },
  decorators: [
    (nextState) => { // $classesIds
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (typeof nextState.$classesIds === 'undefined' ||
        nextSubState.classes !== subState.classes) {
        const $classesIds = nextSubState.classes.map((item) => item.id);

        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {$classesIds});
      }

      return nextState;
    },
    (nextState) => { // $groupInfo
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (typeof nextState.$groupInfo === 'undefined' ||
        nextSubState.group_info !== subState.group_info) {
        const $groupInfo = {};
        Object.values(nextSubState.group_info).map((item) => {
          const fullPath = item.path + item.id + '.';
          let splitPath = fullPath.split('.');
          splitPath.pop();
          // start path from current role's level
          splitPath = splitPath.splice(nextState[sdoNs.SESSION].currentLevel);
          $groupInfo[item.id] = {
            ...item,
            split_path: splitPath.map((id) => parseInt(id, 10))
          };
        });

        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {$groupInfo});
      }

      return nextState;
    },
    (nextState) => { // $classesGroupedBySchool
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (typeof nextState.$classesGroupedBySchool === 'undefined' ||
        nextSubState.classes !== subState.classes ||
        nextSubState.schools !== subState.schools
      ) {
        const classesGroupedBySchool = {};
        nextSubState.classes.map((item, index) => {
          let pathArr = item.path?.split('.');
          const parentId = pathArr[pathArr.length - 2];
          if (!classesGroupedBySchool[parentId]) {
            classesGroupedBySchool[parentId] = [];
          }
          const classInfo = {
            ...item,
            index,
            parent_id: parentId,
          };
          classesGroupedBySchool[parentId].push(classInfo);
        });
        nextSubState.schools.map((id) => {
          if (!classesGroupedBySchool[id]) {
            classesGroupedBySchool[id] = [];
          }
        });
        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {
          $classesGroupedBySchool: classesGroupedBySchool
        });
      }

      return nextState;
    },
    (nextState) => { // $products, $productsByName, $showStatsLink
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (typeof nextState.$products === 'undefined' ||
        nextSubState.products !== subState.products ||
        nextState[sdoNs.SESSION].locale !== state[sdoNs.SESSION].locale) {
        let $showStatsLink = false;
        let $qrEnabled = false;
        const $productsByName = {};
        const $products = [];

        nextSubState.products.forEach((item) => {
          if (
            item.product === statsProducts.MATH_ACADEMY ||
            item.product === statsProducts.ABCMOUSE ||
            item.product === statsProducts.READING_MASTERY ||
            item.product === statsProducts.ABCMOUSE_ENGLISH ||
            item.product === statsProducts.MY_READING_ACADEMY ||
            item.product === statsProducts.MY_READING_ACADEMY_ES ||
            item.product === statsProducts.MY_MATH_ACADEMY
          ) {
            $showStatsLink = true;
          }
          if (
            (item.product === statsProducts.MY_READING_ACADEMY ||
            item.product === statsProducts.MY_MATH_ACADEMY ||
            item.product === statsProducts.MY_READING_ACADEMY_ES) &&
            item.product_id
          ) {
            $qrEnabled = true;
          }
          item = Object.assign({}, item, {
            active: item.status === 'active',
            licenseStartDate: item.start_timestamp ? getLocaleDate(item.start_timestamp * 1000, nextState[sdoNs.SESSION].locale) : null,
            licenseEndDate: item.end_timestamp ? getLocaleDate(item.end_timestamp * 1000, nextState[sdoNs.SESSION].locale) : null,
            redeemDate: item.redeemed_timestamp ? getLocaleDate(item.redeemed_timestamp * 1000, nextState[sdoNs.SESSION].locale) : null
          });
          if (item.product_status !== productStatuses.UNASSIGNED && item.product_status !== productStatuses.EXPIRED) {
            $productsByName[item.product] = item;
            $products.push(item);
          }
        });
        const sortedProducts = getSortedProducts($products);
        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {
          $qrEnabled,
          $products: sortedProducts,
          $productsByName,
          $showStatsLink
        });
      }

      return nextState;
    },
    (nextState) => { // $birthMonth, $birthYear
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (typeof nextState.$birthYear === 'undefined' || nextSubState.birth_date !== subState.birth_date) {
        let $birthYear = null;
        let $birthMonth = null;
        if (typeof nextSubState.birth_date === 'string') {
          const parts = nextSubState.birth_date.split('-');
          [$birthYear, $birthMonth] = parts;
        }

        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {$birthMonth, $birthYear});
      }

      return nextState;
    },
    (nextState) => { // $archivedDate
      const state = storeInstance.getState();
      const subState = state[sdoNs.STUDENT_INFO];
      const nextSubState = nextState[sdoNs.STUDENT_INFO];

      if (
        typeof nextState.$archivedDate === 'undefined'
        ||
        nextSubState.archived_timestamp !== subState.archived_timestamp
      ) {
        let $archivedDate = null;
        if (nextSubState.archived_timestamp) {
          $archivedDate = dateTimeFormatter.format(nextSubState.archived_timestamp * 1000);
        }

        nextState = deepAssign(nextState, sdoNs.STUDENT_INFO, {$archivedDate});
      }

      return nextState;
    },
  ]
};

storeInstance.addState(sdo);
